import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "antd";
import {
  Home,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Copy,
  WifiOff,
  LogOut,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useAppTheme } from "@/shared/lib/theme";
import { CustomButton } from "@/shared/ui";
import { useTranslation } from "react-i18next";

interface ErrorStatePageProps {
  statusCode?: number;
  title?: string;
  description?: string;
  errorPayload?: any;
  onRetry?: () => void;
  onGoHome?: () => void;
  onLogout?: () => void;
}

interface ErrorConfig {
  watermark: string;
  headline: string;
  subtext: string;
  statusCode: string;
  statusMessage: string;
  showOfflineBadge?: boolean;
}

const ErrorStatePage = ({
  statusCode = 404,
  title,
  description,
  errorPayload,
  onRetry,
  onGoHome,
  onLogout,
}: ErrorStatePageProps) => {
    const { t } = useTranslation();
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const { theme } = useAppTheme();

  // Determine variant based on status code
  const getVariant = (): "404" | "403" | "500" | "offline" => {
    if (statusCode === 0 || !navigator.onLine) return "offline";
    if (statusCode === 403) return "403";
    if (statusCode >= 500) return "500";
    return "404";
  };

  const variant = getVariant();

  const variantConfig: Record<string, ErrorConfig> = {
    "404": {
      watermark: "404",
      headline: title || "Oops, something's not right.",
      subtext:
        description ||
        "We couldn't find the page you're looking for. Please try again or return to dashboard.",
      statusCode: "404",
      statusMessage: "Not Found",
    },
    "403": {
      watermark: "403",
      headline: title || "Access denied.",
      subtext:
        description ||
        "You don't have permission to access this resource. Please contact your administrator.",
      statusCode: "403",
      statusMessage: "Forbidden",
    },
    "500": {
      watermark: "500",
      headline: title || "Something went wrong.",
      subtext:
        description ||
        "We encountered an error processing your request. Please try again or return to dashboard.",
      statusCode: "500",
      statusMessage: "Internal Server Error",
    },
    offline: {
      watermark: "OFFLINE",
      headline: title || "You're offline.",
      subtext:
        description ||
        "Please check your internet connection and try again. Some features may be unavailable.",
      statusCode: "0",
      statusMessage: "Network Error",
      showOfflineBadge: true,
    },
  };

  const config = variantConfig[variant];

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = "/";
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const timestamp = errorPayload?.timestamp || new Date().toISOString();
  const endpoint = errorPayload?.endpoint || errorPayload?.path || "N/A";
  const message = errorPayload?.message || config.statusMessage;
  const errors = errorPayload?.errors || [];

  // Prioritize errorPayload.statusCode over statusCode prop for details display
  const displayStatusCode =
    errorPayload?.statusCode ?? statusCode ?? config.statusCode;

  const detailsText = `Status: ${displayStatusCode}
Message: ${message}
Endpoint: ${endpoint}
Timestamp: ${timestamp}${
    errors.length > 0
      ? `\nErrors:\n${errors.map((e: any) => `  - ${e.field}: ${e.message}`).join("\n")}`
      : ""
  }`;

  const copyDetails = () => {
    const jsonPayload = errorPayload
      ? JSON.stringify(errorPayload, null, 2)
      : detailsText;
    navigator.clipboard.writeText(jsonPayload);
  };

  // Render illustration based on variant
  const renderIllustration = () => {
    if (variant === "404") {
      return (
        <svg
          width="240"
          height="240"
          viewBox="0 0 320 320"
          fill="none"
          className="mx-auto"
        >
          {/* Disconnected plug illustration */}
          <g opacity="0.8">
            <rect
              x="40"
              y="120"
              width="100"
              height="80"
              rx="12"
              fill={theme === "dark" ? "#374151" : "#e5e7eb"}
              stroke={theme === "dark" ? "#6b7280" : "#9ca3af"}
              strokeWidth="2"
            />
            <line
              x1="70"
              y1="120"
              x2="70"
              y2="90"
              stroke={theme === "dark" ? "#6b7280" : "#9ca3af"}
              strokeWidth="8"
              strokeLinecap="round"
            />
            <line
              x1="110"
              y1="120"
              x2="110"
              y2="90"
              stroke={theme === "dark" ? "#6b7280" : "#9ca3af"}
              strokeWidth="8"
              strokeLinecap="round"
            />
            <circle cx="90" cy="160" r="6" fill="#0f766e" opacity="0.3" />
          </g>
          <g opacity="0.8">
            <rect
              x="180"
              y="130"
              width="100"
              height="80"
              rx="12"
              fill={theme === "dark" ? "#374151" : "#e5e7eb"}
              stroke={theme === "dark" ? "#6b7280" : "#9ca3af"}
              strokeWidth="2"
            />
            <circle
              cx="205"
              cy="155"
              r="8"
              fill={theme === "dark" ? "#4b5563" : "#d1d5db"}
            />
            <circle
              cx="205"
              cy="185"
              r="8"
              fill={theme === "dark" ? "#4b5563" : "#d1d5db"}
            />
            <circle
              cx="255"
              cy="155"
              r="8"
              fill={theme === "dark" ? "#4b5563" : "#d1d5db"}
            />
            <circle
              cx="255"
              cy="185"
              r="8"
              fill={theme === "dark" ? "#4b5563" : "#d1d5db"}
            />
          </g>
          <line
            x1="140"
            y1="160"
            x2="180"
            y2="170"
            stroke={theme === "dark" ? "#4b5563" : "#d1d5db"}
            strokeWidth="2"
            strokeDasharray="4 4"
          />
        </svg>
      );
    }

    if (variant === "500") {
      return (
        <svg
          width="240"
          height="240"
          viewBox="0 0 320 320"
          fill="none"
          className="mx-auto"
        >
          {/* Broken cloud */}
          <path
            d="M80 140 Q70 120 90 110 Q100 100 120 110 Q140 105 150 120 L155 140 L80 140 Z"
            fill={theme === "dark" ? "#374151" : "#e5e7eb"}
            stroke={theme === "dark" ? "#6b7280" : "#9ca3af"}
            strokeWidth="2"
          />
          <path
            d="M85 180 Q75 165 95 165 L145 165 Q165 165 155 180 L85 180 Z"
            fill={theme === "dark" ? "#374151" : "#e5e7eb"}
            stroke={theme === "dark" ? "#6b7280" : "#9ca3af"}
            strokeWidth="2"
          />
          <line
            x1="90"
            y1="145"
            x2="85"
            y2="160"
            stroke="#ef4444"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <line
            x1="120"
            y1="145"
            x2="120"
            y2="160"
            stroke="#ef4444"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <line
            x1="150"
            y1="145"
            x2="155"
            y2="160"
            stroke="#ef4444"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <g transform="translate(180, 120)">
            <path
              d="M50 20 L80 70 L20 70 Z"
              fill="#fef3c7"
              stroke="#f59e0b"
              strokeWidth="2"
            />
            <line
              x1="50"
              y1="35"
              x2="50"
              y2="52"
              stroke="#f59e0b"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <circle cx="50" cy="60" r="2.5" fill="#f59e0b" />
          </g>
        </svg>
      );
    }

    if (variant === "offline") {
      return (
        <svg
          width="240"
          height="240"
          viewBox="0 0 320 320"
          fill="none"
          className="mx-auto"
        >
          {/* WiFi with slash */}
          <path
            d="M90 180 Q90 140 130 140 Q170 140 170 180"
            fill="none"
            stroke={theme === "dark" ? "#4b5563" : "#d1d5db"}
            strokeWidth="8"
            strokeLinecap="round"
            opacity="0.3"
          />
          <path
            d="M70 165 Q70 120 130 120 Q190 120 190 165"
            fill="none"
            stroke={theme === "dark" ? "#4b5563" : "#d1d5db"}
            strokeWidth="8"
            strokeLinecap="round"
            opacity="0.3"
          />
          <path
            d="M50 150 Q50 100 130 100 Q210 100 210 150"
            fill="none"
            stroke={theme === "dark" ? "#4b5563" : "#d1d5db"}
            strokeWidth="8"
            strokeLinecap="round"
            opacity="0.3"
          />
          <circle
            cx="130"
            cy="190"
            r="8"
            fill={theme === "dark" ? "#6b7280" : "#9ca3af"}
          />
          <line
            x1="40"
            y1="90"
            x2="220"
            y2="210"
            stroke="#ef4444"
            strokeWidth="8"
            strokeLinecap="round"
          />
          <line
            x1="40"
            y1="90"
            x2="220"
            y2="210"
            stroke="#fee2e2"
            strokeWidth="4"
            strokeLinecap="round"
          />
        </svg>
      );
    }

    // Default 403
    return (
      <svg
        width="240"
        height="240"
        viewBox="0 0 320 320"
        fill="none"
        className="mx-auto"
      >
        {/* Lock icon */}
        <rect
          x="100"
          y="150"
          width="120"
          height="100"
          rx="12"
          fill={theme === "dark" ? "#374151" : "#e5e7eb"}
          stroke={theme === "dark" ? "#6b7280" : "#9ca3af"}
          strokeWidth="3"
        />
        <path
          d="M130 150 V120 Q130 90 160 90 Q190 90 190 120 V150"
          fill="none"
          stroke={theme === "dark" ? "#6b7280" : "#9ca3af"}
          strokeWidth="3"
        />
        <circle cx="160" cy="190" r="12" fill="#ef4444" />
        <line
          x1="160"
          y1="202"
          x2="160"
          y2="220"
          stroke="#ef4444"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>
    );
  };

  const pageTitles: Record<string, string> = {
    "404": "Page Not Found | Storemandu",
    "403": "Access Denied | Storemandu",
    "500": "Server Error | Storemandu",
    offline: "Offline | Storemandu",
  };

  return (
    <div
      data-testid="error-state-page"
      data-status-code={String(displayStatusCode)}
      className={`min-h-screen flex items-center justify-center px-6 py-12 ${
        theme === "dark" ? "bg-[#0a0f1a]" : "bg-white"
      }`}
    >
      <Helmet>
        <title>{pageTitles[variant]}</title>
        <meta name="description" content={config.subtext} />
      </Helmet>
      <div className="max-w-2xl w-full">
        {/* Vertical Layout */}
        <div className="flex flex-col items-center text-center">
          {/* Status Badge (for offline variant) */}
          {config.showOfflineBadge && (
            <div
              className={"inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 bg-surface border"}
            >
              <WifiOff
                className={"w-3.5 h-3.5 text-secondary"}
              />
              <span
                className={"text-xs font-medium text-primary"}
              >
                {t("errorState.page.offline")}</span>
            </div>
          )}

          {/* Large Watermark Background */}
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] dark:opacity-[0.02]">
              <span
                className={"text-[160px] md:text-[320px] font-bold leading-none select-none text-primary"}
              >
                {config.watermark}
              </span>
            </div>

            {/* Illustration */}
            <div className="relative z-10 py-8">{renderIllustration()}</div>
          </div>

          {/* Headline */}
          <h1
            className="text-2xl md:text-3xl font-bold mb-4 text-primary"
          >
            {config.headline}
          </h1>

          {/* Subtext */}
          <p
            className="text-base md:text-lg mb-8 max-w-md text-secondary"
          >
            {config.subtext}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6 w-full sm:w-auto">
            {onLogout ? (
              <CustomButton
                type="primary"
                size="large"
                icon={<LogOut className="w-4 h-4" />}
                onClick={onLogout}
                className="w-full sm:w-auto"
              >
                {t("errorState.page.signOut")}</CustomButton>
            ) : (
              <>
                <CustomButton
                  type="primary"
                  size="large"
                  icon={<Home className="w-4 h-4" />}
                  onClick={handleGoHome}
                  className="w-full sm:w-auto"
                >
                  {t("errorState.page.goToDashboard")}</CustomButton>
                <Button
                  size="large"
                  icon={<RefreshCw className="w-4 h-4" />}
                  onClick={handleRetry}
                  className={"w-full sm:w-auto border-strong"}
                >
                  {t("errorState.page.tryAgain")}</Button>
              </>
            )}
          </div>

          {/* Show Details Toggle */}
          <button
            onClick={() => setDetailsExpanded(!detailsExpanded)}
            className={"flex items-center gap-2 text-sm transition-colors group text-secondary hover:text-brand"}
          >
            <span className="font-medium">{t("errorState.page.showDetails")}</span>
            {detailsExpanded ? (
              <ChevronUp className="w-4 h-4 group-hover:translate-y-[-2px] transition-transform" />
            ) : (
              <ChevronDown className="w-4 h-4 group-hover:translate-y-[2px] transition-transform" />
            )}
          </button>

          {/* Details Panel (Collapsible with Animation) */}
          <AnimatePresence>
            {detailsExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{
                  duration: 0.3,
                  ease: "easeInOut",
                }}
                className="w-full overflow-hidden"
              >
                <div
                  className={"mt-4 p-4 rounded-lg border w-full text-left bg-surface"}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span
                      className={"text-eyebrow"}
                    >
                      {t("errorState.page.errorDetails")}</span>
                    <button
                      onClick={copyDetails}
                      className={"flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors text-secondary hover:text-brand hover:bg-surface-elevated"}
                    >
                      <Copy className="w-3.5 h-3.5" />
                      {t("errorState.page.copy")}</button>
                  </div>
                  <pre
                    className={"text-xs font-mono whitespace-pre-wrap break-all text-primary"}
                  >
                    {detailsText}
                  </pre>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default ErrorStatePage;
